import React from 'react';
import { Navigate } from 'react-router-dom';
import SidebarParametres from '../../../layout/SidebarParametres';
import { useOAD } from '../../../layout/OADLayout';
import { api } from '../../shared/api/api';
import { useApiData } from '../../shared/hooks/useFetch';
import GraphOptiFinanciere from '../../shared/charts/Graphe_opti_financiere';
import ResistanceCartes from './ResistanceCartes';

/**
 * Optimisation assurance (2026-2027) — port de optimisation2.js, quasi à
 * l'identique. Seul changement : les boutons "Coût net" / "Coût brut" sont
 * devenus une liste déroulante dans la sidebar. Mêmes routes qu'avant :
 * page/optimisation (les deux graphiques) et page/exploitation (produit brut).
 *
 * Trois cartes : l'optimisation, puis les deux cartes de la page Résistance
 * (tableau d'indemnisation, puis partie haute). Plus d'affichage conditionnel
 * selon le type de compte, ni d'offres, ni de vidéo.
 */

const COUTS = [
  { value: 'CN', label: 'Coût net' },
  { value: 'CB', label: 'Coût brut' },
];

// Mise en forme du retour de page/optimisation, reprise telle quelle de
// l'ancienne page (y compris les espaces finaux des clés et libellés "Prix",
// qui font partie des données que renvoie l'API). Chaque série = [x, y, taille]
// en %, suivi des deux libellés lus par le graphique.
const pct = (v) => 100 * Math.round(v * 1000) / 1000;
const pt = (dataGraph, cle, nom, option) => [pct(dataGraph[cle][0]), pct(dataGraph[cle][1]), pct(dataGraph[cle][2]), nom, option];

function mettreEnForme(d) {
  return [
    [pt(d, 'Sans Assurance', 'Sans ass', 'Sans assurance')],
    [pt(d, 'MRC 20%', 'MRC 20%', 'Sans option'), pt(d, 'MRC 25%', 'MRC 25%', 'Sans option')],
    [pt(d, 'MRC 20% + Prix 60% ', 'MRC 20%', 'Rachat prix 60% '), pt(d, 'MRC 25% + Prix 60% ', 'MRC 25%', 'Rachat prix 60% ')],
    [pt(d, 'MRC 20% + Prix 110% ', 'MRC 20%', 'Rachat prix 110% '), pt(d, 'MRC 25% + Prix 110% ', 'MRC 25%', 'Rachat prix 110% ')],
    [pt(d, 'MRC 20% + Prix 120% ', 'MRC 20%', 'Rachat prix 120% '), pt(d, 'MRC 25% + Prix 120% ', 'MRC 25%', 'Rachat prix 120% ')],
    [pt(d, 'MRC 20% + Rendement 90%', 'MRC 20%', 'Rachat rendement 90%'), pt(d, 'MRC 25% + Rendement 90%', 'MRC 25%', 'Rachat rendement 90%')],
    [pt(d, 'MRC 20% + Rendement 105%', 'MRC 20%', 'Rachat rendement 105%'), pt(d, 'MRC 25% + Rendement 105%', 'MRC 25%', 'Rachat rendement 105%')],
    [pt(d, 'MRC 20% + Rendement 110%', 'MRC 20%', 'Rachat rendement 110% '), pt(d, 'MRC 25% + Rendement 110%', 'MRC 25% ', 'Rachat rendement 110% ')],
    [pt(d, 'MRC 20% + Rendement 110% + Prix 120%', 'MRC 20%', 'Rachat rendement 110%, rachat prix 120%'),
      pt(d, 'MRC 25% + Rendement 110% + Prix 120%', 'MRC 25%', 'Rachat rendement 110%, rachat prix 120%')],
  ];
}

export default function OptimisationAssurance() {
  const { idExploit } = useOAD();
  const [cout, setCout] = React.useState('CN');
  const [versAccueil, setVersAccueil] = React.useState(false);

  const parametres = React.useMemo(() => ({ idExploitation: idExploit }), [idExploit]);
  const { data: optimisation, error: erreurOpti } = useApiData(api.getOptimisation, parametres);
  const { data: exploitation } = useApiData(api.getExploitation, parametres);

  const graphes = React.useMemo(
    () => (optimisation?.dataGraphCN && optimisation?.dataGraphCB
      ? { CN: mettreEnForme(optimisation.dataGraphCN), CB: mettreEnForme(optimisation.dataGraphCB) }
      : null),
    [optimisation]
  );
  const PBTot = exploitation?.PBTotal ?? 0;

  // L'API répond par une chaîne ("Exploitation X non trouvée") au lieu d'un
  // objet quand l'exploitation n'existe pas : même réaction qu'avant, on vide
  // la session et on retourne à l'accueil.
  const introuvable = typeof optimisation === 'string' || typeof exploitation === 'string';
  React.useEffect(() => {
    if (introuvable) {
      localStorage.clear();
      setVersAccueil(true);
    }
  }, [introuvable]);

  if (versAccueil) return <Navigate push to="/" />;

  // Bornes des axes, calculées comme avant sur la série "coût brut".
  let bornes = null;
  if (graphes) {
    const CB = graphes.CB;
    const maxX = Math.round(CB[8][0][0]) % 2 === 0 ? Math.round(CB[8][0][0]) + 2 : Math.round(CB[8][0][0]) + 1;
    const maxY = Math.round(CB[0][0][1]) % 2 === 0 ? Math.round(CB[0][0][1]) + 2 : Math.round(CB[0][0][1]) + 1;
    bornes = { minX: 0, minY: 0, maxX, maxY };
  }

  return (
    <div className="oad-contenu">
      <SidebarParametres niveau1="annee-en-cours" niveau2="optimisation-assurance" niveau3={null} idExploit={idExploit}>
        <label className="sidebar-champ">
          <span className="sidebar-label">Coût</span>
          <select value={cout} onChange={(e) => setCout(e.target.value)}>
            {COUTS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </label>
      </SidebarParametres>

      <main className="oad-cards">
        {/* Carte 1 : optimisation (ex optimisation2.js) */}
        <section className="oad-card">
          <div className="rowResiScalePrincipal" style={{ alignItems: 'flex-start' }}>
            <div className="optiLeft">
              <div className="titrePlusVideo">
                <div className="titre" style={{ marginTop: 0 }}> Comment choisir mon assurance ? </div>
              </div>
              <div className="paragraphe" style={{ marginBottom: '5px' }}> Sur le graphique, données sont calculées pour un risque moyen : </div>
              <ul style={{ marginBottom: '5px', marginTop: '5px' }}>
                <li><div className="lila paragraphe"> À l’horizontal, le coût de l’assurance en % du produit brut </div></li>
                <ul>
                  <li><div className="lila"> <span style={{ fontWeight: 'bold' }}> Coût brut = </span> montant annuel de la prime d'assurance </div></li>
                  <li><div className="lila"> <span style={{ fontWeight: 'bold' }}>Coût net = </span> montant annuel de la prime - les subventions </div></li>
                </ul>
                <li><div className="lila paragraphe"> A la verticale, le <span style={{ fontWeight: 'bold' }}>reste à charge</span> après indemnisation, calculé en moyenne et en % du produit brut </div></li>
              </ul>
              <div className="paragraphe" style={{ marginBottom: '10px' }}> Les rond avec un <span style={{ fontWeight: 'bold' }}> intérieur blanc </span>correspondent à l'assurance <span style={{ fontWeight: 'bold' }}>MRC 20%</span>, ceux <span style={{ fontWeight: 'bold' }}> sans</span> correspondent à l'assurance <span style={{ fontWeight: 'bold' }}>MRC 25%</span>. </div>
              <div className="paragraphe" style={{ marginBottom: '5px' }}> Pour lire les détails, passer sur le point avec la souris : </div>
              <div className="paragraphe"> <span style={{ fontWeight: 'bold' }}>RAC = </span> reste à charge moyen après indemnisation = <b> risque moyen - indemnisations</b></div>
              <div className="paragraphe"> <span style={{ fontWeight: 'bold' }}>C = </span>coût net ou coût brut </div>
            </div>
            <div className="optiRight">
              <div className="titreMoins row" style={{ marginTop: 0, marginBottom: 0 }}> Ratio coût-sécurité (en % de produit brut) </div>
              <div className="titreMoins row" style={{ marginTop: 0, marginBottom: 0, fontWeight: 'normal' }}> Plus le reste à charge est faible plus je suis sécurisé </div>
              {erreurOpti && <p className="tdb-erreur">{erreurOpti}</p>}
              {graphes ? (
                <>
                  <div className="paragraphe row" style={{ marginTop: '5px', marginBottom: 0 }}>
                    Produit brut de référence : {PBTot.toLocaleString()}€ / Risque moyen : {graphes.CN[0][0][1]} %
                  </div>
                  <GraphOptiFinanciere data={graphes[cout]} print={false} cout={cout} {...bornes} />
                </>
              ) : (
                !erreurOpti && <p className="tdb-chargement">Chargement…</p>
              )}
            </div>
          </div>
        </section>

        {/* Cartes 2 et 3 : Résistance (tableau d'indemnisation, puis partie haute) */}
        <ResistanceCartes idExploit={idExploit} />
      </main>
    </div>
  );
}
